/**
 * Month Selector - Calendar-specific month selection
 * Replaces ReportingPeriodBar for Calendar tab
 * Allows selection of Month + Year only (no partial months)
 * Uses controlled filter pattern: dropdowns update local state, Apply button triggers change
 *
 * When yearOptions is provided (data-derived years), Year dropdown uses those only;
 * otherwise falls back to fixed range (2021 .. current+2).
 */
import { useState, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext.jsx';
import MonthYearPicker from './MonthYearPicker.jsx';

export default function MonthSelector({ selectedMonth, onMonthChange, disabled = false, yearOptions, debug = false, onApply }) {
  const { theme } = useTheme();
  
  const currentYear = new Date().getFullYear();
  const years = (Array.isArray(yearOptions) && yearOptions.length > 0)
    ? yearOptions
    : (() => {
        const arr = [];
        const minYear = 2021;
        const maxYear = currentYear + 2;
        for (let y = minYear; y <= maxYear; y++) arr.push(y);
        return arr;
      })();
  
  const months = [
    { value: 0, label: 'January' },
    { value: 1, label: 'February' },
    { value: 2, label: 'March' },
    { value: 3, label: 'April' },
    { value: 4, label: 'May' },
    { value: 5, label: 'June' },
    { value: 6, label: 'July' },
    { value: 7, label: 'August' },
    { value: 8, label: 'September' },
    { value: 9, label: 'October' },
    { value: 10, label: 'November' },
    { value: 11, label: 'December' },
  ];
  
  // Derive applied values directly from selectedMonth prop (source of truth)
  const appliedDate = selectedMonth instanceof Date ? selectedMonth : (selectedMonth ? new Date(selectedMonth) : new Date());
  const appliedMonthIndex = appliedDate.getMonth(); // 0-11 (JavaScript Date month index)
  const appliedYear = appliedDate.getFullYear();
  
  // Create stable key for useEffect dependency (tracks actual date value, not computed values)
  const appliedKey = isNaN(appliedDate.getTime()) ? '' : String(appliedDate.getTime());
  
  // Pending values (local state, updated by dropdowns)
  // Initialize from applied values using function form to ensure correct initial state
  // Ensure values are always valid numbers (never undefined/null)
  const [pendingYear, setPendingYear] = useState(() => {
    const year = Number.isInteger(appliedYear) ? appliedYear : new Date().getFullYear();
    return year;
  });
  const [pendingMonthIndex, setPendingMonthIndex] = useState(() => {
    const month = Number.isInteger(appliedMonthIndex) && appliedMonthIndex >= 0 && appliedMonthIndex <= 11 
      ? appliedMonthIndex 
      : new Date().getMonth();
    return month;
  });
  
  // Sync pending values whenever selectedMonth prop changes (reliable dependency)
  useEffect(() => {
    if (!isNaN(appliedDate.getTime()) && Number.isInteger(appliedYear) && Number.isInteger(appliedMonthIndex)) {
      setPendingYear(appliedYear);
      setPendingMonthIndex(appliedMonthIndex);
    }
  }, [appliedKey, appliedYear, appliedMonthIndex]); // Include appliedYear and appliedMonthIndex for clarity
  
  const handleYearChange = (e) => {
    const newYear = parseInt(e?.target?.value, 10);
    if (!Number.isInteger(newYear)) return;
    setPendingYear(newYear);
    // Do NOT call onMonthChange - wait for Apply button
  };
  
  const handleMonthChange = (e) => {
    const newMonthIndex = parseInt(e?.target?.value, 10);
    if (!Number.isInteger(newMonthIndex) || newMonthIndex < 0 || newMonthIndex > 11) return;
    setPendingMonthIndex(newMonthIndex);
    // Do NOT call onMonthChange - wait for Apply button
  };
  
  const handleApply = () => {
    // Validate pending values before creating Date
    if (!Number.isInteger(pendingYear) || !Number.isInteger(pendingMonthIndex)) return;
    if (pendingMonthIndex < 0 || pendingMonthIndex > 11) return;
    
    // Construct new date using 0-11 month index (JavaScript Date standard)
    const newDate = new Date(pendingYear, pendingMonthIndex, 1);
    
    // Validate the created Date
    if (isNaN(newDate.getTime())) return;
    
    if (typeof onApply === 'function') onApply(appliedDate, newDate);
    if (onMonthChange) onMonthChange(newDate);
  };
  
  const handleToday = () => {
    const today = new Date();
    const todayMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    if (onMonthChange) onMonthChange(todayMonth);
    // Today button immediately applies (no pending state)
  };
  
  // Validate pending values and check if they differ from applied values
  const isPendingValid = Number.isInteger(pendingYear) && Number.isInteger(pendingMonthIndex) && 
                         pendingMonthIndex >= 0 && pendingMonthIndex <= 11;
  const isDirty = pendingYear !== appliedYear || pendingMonthIndex !== appliedMonthIndex;
  const hasPendingChanges = isPendingValid && isDirty;
  
  return (
    <div style={{
      ...styles.container,
      borderBottomColor: theme === 'dark' ? 'var(--color-border-light-dark)' : 'var(--color-border-light)',
    }}>
      <div className="month-selector-content" style={styles.content}>
        <MonthYearPicker
          id="month-selector-month"
          label="Month"
          value={String(pendingMonthIndex)}
          options={months.map(m => ({ value: String(m.value), label: m.label }))}
          onChange={handleMonthChange}
          disabled={disabled}
          ariaLabel="Select month"
        />
        
        <div
          style={{ display: 'inline-flex', alignItems: 'center' }}
          {...(debug && years.length ? { 'data-debug-year-options': years.join(',') } : {})}
        >
          <MonthYearPicker
            id="month-selector-year"
            label="Year"
            value={String(pendingYear)}
            options={years.map(y => ({ value: String(y), label: String(y) }))}
            onChange={handleYearChange}
            disabled={disabled}
            ariaLabel="Select year"
          />
        </div>
        
        <button
          onClick={handleApply}
          disabled={!hasPendingChanges || disabled}
          className={hasPendingChanges && !disabled ? 'owner-button-primary' : 'owner-button-secondary'}
          style={{
            ...styles.applyButton,
            opacity: hasPendingChanges && !disabled ? 1 : 0.6,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          aria-label="Apply month selection"
        >
          Apply
        </button>
        
        <button
          onClick={handleToday}
          disabled={disabled}
          className="owner-button-secondary"
          style={{
            ...styles.todayButton,
            opacity: disabled ? 0.6 : 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          aria-label="Go to current month"
        >
          Today
        </button>
      </div>
    </div>
  );
}

const styles = {
  container: {
    marginBottom: 0,
    paddingBottom: 0,
    borderBottom: 'none',
  },
  content: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--spacing-3)',
    flexWrap: 'wrap',
    // Ensure all children align to center baseline
    alignContent: 'center',
  },
  label: {
    fontSize: 'var(--font-size-sm)',
    fontWeight: 'var(--font-weight-medium)',
    color: 'var(--color-text-secondary)',
    whiteSpace: 'nowrap',
  },
  select: {
    padding: 'var(--spacing-2) var(--spacing-3)',
    borderRadius: 'var(--radius-sm)',
    border: '1px solid var(--color-border-medium)',
    fontSize: 'var(--font-size-sm)',
    cursor: 'pointer',
    height: '36px',
    minWidth: '140px',
  },
  applyButton: {
    height: '36px',
    whiteSpace: 'nowrap',
    // Other styles (padding, border-radius, font-size, etc.) come from owner-button classes
  },
  todayButton: {
    height: '36px',
    whiteSpace: 'nowrap',
    // Other styles (padding, border-radius, font-size, etc.) come from owner-button classes
  },
};
